import {
  AvailableLanguages,
  Clinic,
  LeadSystemSource,
  ProgramType,
  scheduleClient,
  SchedulingWorkflow,
  Step,
  User,
} from '@enaratech/funnel-helper';
import { Dispatch } from 'react';
import Toast from 'src/components/Common/Toast/Toast';
import {
  RecentlyBookedAppointments,
  SET_SSB_INFO,
  SSBActionTypes,
  SSBFlowState,
} from 'src/contexts/ssb/types';
import { capitalizeToKebabCase } from 'src/utils/array';
import { getEffectiveProgramType } from 'src/utils/programType';
import { reconcileStepsWithRecentBookings } from '../../appointments';

export const initializeSchedulingAction = async ({
  context,
  setters,
  setState,
}: {
  context: {
    clinic: Clinic;
    user: User;
    stepsRef: React.MutableRefObject<Step[] | null>;
    selectedLanguage: AvailableLanguages | null;
    recentlyBookedAppointments: RecentlyBookedAppointments;
  };
  setters: {
    setSelectedLanguage: (value: React.SetStateAction<AvailableLanguages | null>) => void;
  };
  setState: Dispatch<SSBActionTypes>;
}) => {
  const fetchedSteps = await scheduleClient.getStepsForMember({
    clinicId: context.clinic!.details.clinicId,
    memberId: context.user!.id,
    programType: getEffectiveProgramType({ user: context.user, clinic: context.clinic }) as
      | ProgramType.InClinic
      | ProgramType.Remote,
    systemSource: capitalizeToKebabCase(context.user!.systemSource) as LeadSystemSource,
    schedulingWorkflow: SchedulingWorkflow.SelfServeBooking,
  });

  if (!fetchedSteps) {
    return Toast.notification(
      'error',
      'There was an error obtaining the information. Please reload the page or wait for a support member to contact you, we are looking for the solution.'
    );
  }

  // Reconcile with funnel-local recent bookings BEFORE deriving the active step. Without this, a
  // step just booked through the funnel — which the backend has not yet reflected (Acuity webhook
  // lag) — is re-marked active by getStepsForMember and offered again, producing a duplicate
  // appointment. (goToNextStepAction patches the same way, but its result is overwritten here on
  // the very next fetch, so the reconciliation must happen in this action too.)
  const recentKeys = Object.keys(context.recentlyBookedAppointments ?? {});
  const reconciledSteps = reconcileStepsWithRecentBookings(fetchedSteps, recentKeys);

  if (reconciledSteps.every((step) => step.scheduled || step.skipped)) {
    setState({ type: SET_SSB_INFO, payload: SSBFlowState.AllScheduled });
    return;
  }

  // Set steps BEFORE checking language so currentStep is available
  context.stepsRef.current = reconciledSteps;

  // Get the current step from the reconciled steps
  const currentStep = reconciledSteps.find((s) => s.active) || null;

  if (currentStep && !context.selectedLanguage) {
    const { allowedLanguages } = currentStep.config;

    if (allowedLanguages.length === 1) {
      // Only one language available - set it automatically and proceed
      setters.setSelectedLanguage(allowedLanguages[0]);
    } else {
      // Multiple languages - show language selector modal
      setState({ type: SET_SSB_INFO, payload: SSBFlowState.WaitingForUserSelection });
      return;
    }
  }

  setState({ type: SET_SSB_INFO, payload: SSBFlowState.FetchAppointmentsAvailability });
};
